import type { ChangeEvent, KeyboardEventHandler } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import type { UseFormReturn } from 'react-hook-form';
import { maxLength, minLength, regex } from '../../utils/validation';
import type { OrderPaymentInstrument } from '../../bff-api/baskets/model/OrderPaymentInstrument';
import type { FieldConfig } from '../field';

export const messages = defineMessages({
    required: { defaultMessage: '{field} is required', id: 'error.field.required' },
    maxLength: {
        defaultMessage: 'No more than {max} characters',
        id: 'error.field.maxLength',
    },
    minLength: {
        defaultMessage: 'Must be {length} characters',
        id: 'error.field.minLength',
    },
    cardNumber: { defaultMessage: 'Card Number', id: 'use_gift_card_fields.label.card_number' },
    pin: { defaultMessage: 'Pin', id: 'use_gift_card_fields.label.pin' },
    amount: { defaultMessage: 'Custom $10-$500', id: 'use_gift_card_fields.label.amount' },
    wrongAmount: {
        defaultMessage: 'Please select or enter an amount from $10 - $500.',
        id: 'use_gift_card_fields.error.wrong_amount',
    },
    recipientName: {
        defaultMessage: 'Recipient Name',
        id: 'use_gift_card_fields.label.recepient_name',
    },
    recipientEmail: {
        defaultMessage: 'Recipient Email',
        id: 'use_gift_card_fields.label.recipient_email',
    },
    senderName: {
        defaultMessage: 'Sender Name (optional)',
        id: 'use_gift_card_fields.label.sender_name',
    },
    recipientNameRequired: {
        defaultMessage: 'Recipient name field is required',
        id: 'use_gift_card_fields.error.recipient_name_required',
    },
    recipientEmailLabel: {
        defaultMessage: 'Recipient Email',
        id: 'use_gift_card_fields.label.recipient_email',
    },
    recipientEmailRequired: {
        defaultMessage: 'Recipient email field is required',
        id: 'use_gift_card_fields.error.recipient_email_required',
    },
    myEmailLabel: {
        defaultMessage: 'My Email',
        id: 'use_gift_card_fields.label.my-email',
    },
    myEmailRequired: {
        defaultMessage: 'My Email field is required',
        id: 'use_gift_card_fields.error.my_email_required',
    },
    sendToMeToggleLabel: {
        defaultMessage: 'Send to me',
        id: 'use_gift_card_fields.label.send_to_me',
    },
    sendToMeToggleDescription: {
        defaultMessage: 'Send e-gift card to my email',
        id: 'use_gift_card_fields.description.send_to_me_description',
    },
    invalidEmail: {
        defaultMessage: 'Please enter a valid email address.',
        id: 'use_gift_card_fields.error.invalid_email',
    },
    giftCardMessageSpecialCharacters: {
        defaultMessage: "Special characters aren't allowed.",
        id: 'use_gift_card_fields.error.gift_card_message_special_characters',
    },
    giftCardMessageTooltip: {
        id: 'use_gift_card_fields.label.gift_card_message_tooltip',
        defaultMessage:
            'Certain special characters (~@^*()=+[]&#123;&#125;|"><>/) and emojis are not allowed.',
    },
});

const inputProps =
    (maxValueLength: number, onKeyDown?: KeyboardEventHandler<HTMLInputElement>) =>
    ({
        onChange,
    }: {
        onChange: (cc: string) => void;
    }): {
        onChange: (evt: ChangeEvent<HTMLInputElement>) => void;
        inputMode: string;
        onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
    } => ({
        onKeyDown,
        onChange(evt: ChangeEvent<HTMLInputElement>): void {
            if (evt.target.value.length > maxValueLength) {
                evt.preventDefault();
                return;
            }

            onChange(evt.target.value.replace(regex.giftCard, ''));
        },
        inputMode: 'numeric',
    });

export function useGiftCardFields<T extends OrderPaymentInstrument>(
    form: UseFormReturn<T>,
    onKeyDown?: KeyboardEventHandler<HTMLInputElement>,
    sendToMe?: boolean,
): Record<string, FieldConfig> {
    const {
        control,
        formState: { errors },
    } = form;
    const intl = useIntl();

    const fields: Record<string, FieldConfig> = {
        c_GiftCardNumber: {
            id: 'gift-card-card-number-field',
            name: 'c_GiftCardNumber',
            label: intl.formatMessage(messages.cardNumber),
            defaultValue: '',
            type: 'text',
            rules: {
                required: intl.formatMessage(messages.required, { field: 'Card number' }),
                minLength: {
                    value: minLength.giftCardNumber,
                    message: intl.formatMessage(messages.minLength, {
                        length: minLength.giftCardNumber,
                    }),
                },
                maxLength: {
                    value: maxLength.giftCardNumber,
                    message: intl.formatMessage(messages.maxLength, {
                        max: maxLength.giftCardNumber,
                    }),
                },
            },
            inputProps: inputProps(maxLength.giftCardNumber, onKeyDown),
            error: errors.c_GiftCardNumber,
            control,
            variant: 'new-style',
        },
        c_GiftCardPin: {
            id: 'gift-card-pin-field',
            name: 'c_GiftCardPin',
            label: intl.formatMessage(messages.pin),
            defaultValue: '',
            type: 'text',
            rules: {
                required: intl.formatMessage(messages.required, { field: 'Pin' }),
                maxLength: {
                    value: maxLength.giftCardPin,
                    message: intl.formatMessage(messages.maxLength, {
                        max: maxLength.giftCardPin,
                    }),
                },
            },
            inputProps: inputProps(maxLength.giftCardPin, onKeyDown),
            error: errors.c_GiftCardPin,
            control,
            variant: 'new-style',
        },
        giftCardAmount: {
            id: 'gift-card-amount-field',
            name: 'giftCardAmount',
            placeholder: intl.formatMessage(messages.amount),
            defaultValue: '',
            type: 'text',
            rules: {
                min: {
                    value: 10,
                    message: intl.formatMessage(messages.wrongAmount),
                },
                max: {
                    value: 500,
                    message: intl.formatMessage(messages.wrongAmount),
                },
            },
            control,
            error: errors.giftCardAmount,
        },
        giftCardRecipientName: {
            id: 'gift-card-recipient-name',
            name: 'giftCardRecipientName',
            label: intl.formatMessage(messages.recipientName),
            defaultValue: '',
            type: 'text',
            control,
            rules: {
                required: intl.formatMessage(messages.recipientNameRequired),
            },
            inputProps: {
                maxLength: maxLength.giftCardName,
            },
            error: errors.giftCardRecipientName,
            variant: 'new-style',
        },
        giftCardRecipientEmail: {
            id: 'gift-card-recipient-email',
            name: 'giftCardRecipientEmail',
            label: sendToMe
                ? intl.formatMessage(messages.myEmailLabel)
                : intl.formatMessage(messages.recipientEmailLabel),
            defaultValue: '',
            type: 'text',
            control,
            rules: {
                required: sendToMe
                    ? intl.formatMessage(messages.myEmailRequired)
                    : intl.formatMessage(messages.recipientEmailRequired),
                pattern: {
                    value: regex.email,
                    message: intl.formatMessage(messages.invalidEmail),
                },
            },
            inputProps: {
                maxLength: maxLength.giftCardEmail,
            },
            error: errors.giftCardRecipientEmail,
            variant: 'new-style',
        },
        giftCardSenderName: {
            id: 'gift-card-sender-name',
            name: 'giftCardSenderName',
            label: intl.formatMessage(messages.senderName),
            defaultValue: '',
            type: 'text',
            control,
            rules: {},
            inputProps: {
                maxLength: maxLength.giftCardName,
            },
            error: errors.giftCardSenderName,
            variant: 'new-style',
        },
    };

    return fields;
}
